import {
    installmentsTypes,
    schemeTypes,
} from "../config/appliedSchemesConfig";
import {
    capitalSubsidySecondInstallmentDocuments,
    insertAssistanceDocuments,
} from "../config/incentiveClaimFormUtils";
import { formatKeyNames } from "./utils";

/**
 * Produces Excel rows EXACTLY as View
 * - Section + SubSection separated
 * - Tables rendered downwards
 * - Hidden fields excluded
 */
export function normalizeApplicationForExcel({
    sections,
    formData,
    incentiveClaimId,
    installmentType,
}) {
    const rows = [];

    sections.forEach((section) => {
        let sectionHasData = false;

        // Section-level fields → SubSection = General
        sectionHasData =
            processFields({
                fields: section.fields,
                rows,
                formData,
                sectionTitle: typeof section.title === "object" ? section.title.en : section.title,
                subSectionTitle: "General",
            }) || sectionHasData;

        section.subSections?.forEach((sub) => {
            if (
                typeof sub.title === "object" ? sub.title.en.includes("Stamp Duty") : sub.title.includes("Stamp Duty") &&
                String(incentiveClaimId) === schemeTypes.interestassistance.value
            )
                return;

            if (
                typeof sub.title === "object" ? sub.title.en.includes("Tax Documents") : sub.title.includes("Tax Documents") &&
                (installmentType === installmentsTypes.first.value ||
                    String(incentiveClaimId) === schemeTypes.interestassistance.value)
            )
                return;

            let visibleFields = [...(sub.fields || [])];

            visibleFields = visibleFields.filter(
                (f) =>
                    !(
                        capitalSubsidySecondInstallmentDocuments.includes(f.name) &&
                        String(incentiveClaimId) !==
                        schemeTypes.sreimbursementcapitalincentive.value
                    )
            );

            visibleFields = visibleFields.filter(
                (f) =>
                    !(
                        insertAssistanceDocuments.includes(f.name) &&
                        String(incentiveClaimId) !== schemeTypes.interestassistance.value
                    )
            );

            const subHasData = processFields({
                fields: visibleFields,
                rows,
                formData,
                sectionTitle: typeof section.title === "object" ? section.title.en : section.title,
                subSectionTitle: typeof sub.title === "object" ? sub.title.en : sub.title,
            });

            sectionHasData = sectionHasData || subHasData;
        });

        if (!sectionHasData) {
            while (
                rows.length &&
                rows[rows.length - 1].Section === typeof section.title === "object" ? section.title.en : section.title
            ) {
                rows.pop();
            }
        }
    });

    return rows;
}

/**
 * Adds rows and returns true if data exists
 */
function processFields({
    fields,
    rows,
    formData,
    sectionTitle,
    subSectionTitle,
}) {
    if (!fields || !fields.length) return false;
    let hasData = false;

    fields.forEach((field) => {
        const value = formData[field.name];
        if (value === undefined || value === null || value === "") return;

        // 📁 File
        if (typeof value === "object" && (value.url || value.id || value.name)) {
            rows.push({
                Section: sectionTitle,
                SubSection: subSectionTitle,
                Field: typeof field.label === "object" ? field.label.en : field.label || formatKeyNames(field.name),
                __type: "FILE", // mark as file row
                Value: value.url
                    ? { url: value.url, name: value.name || "View File" }
                    : "Not Uploaded",
            });
            hasData = true;
            return;
        }

        // 📊 TABLE (rendered downwards, keys bold, values normal, file links handled)
        if (Array.isArray(value) && value.length > 0) {
            value.forEach((rowObj, objIdx) => {
                const entries = Object.entries(rowObj);

                entries.forEach(([key, val], entryIdx) => {
                    const isLastEntry = entryIdx === entries.length - 1; // last key/value in this object
                    const formattedKey = key
                        .split("_")
                        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                        .join(" ");

                    const isFile =
                        typeof val === "string" &&
                        /\.(pdf|docx?|xlsx?|jpg|png)$/i.test(val);

                    rows.push({
                        Section: sectionTitle,
                        SubSection: subSectionTitle,
                        Field: formattedKey,
                        Value: isFile ? { url: val, name: val.split("/").pop() } : val ?? "--",
                        __type: isFile ? "FILE" : "TABLE",
                        __lastInBlock: isLastEntry, // only true for the last key/value pair of this object
                    });
                });
            });
            hasData = true;
            return;
        }

        // 🔹 Normal field
        rows.push({
            Section: sectionTitle,
            SubSection: subSectionTitle,
            Field: typeof field.label === "object" ? field.label.en : field.label || formatKeyNames(field.name),
            Value: value,
        });
        hasData = true;
    });

    return hasData;
}
